// GtEmailModal (C1-EMAIL) — "Email 40 people", or one, or a reply.
// Compose (from, template, subject, message with merge fields), check
// (everyone listed with reasons to think twice; everyone stays ticked, Jimmy
// unticks), then send. Sending runs on the server at a steady pace, so this box
// can be closed; progress shows while it stays open.
//
// Props: candidateIds, source { kind, id, label }, authFetch, onClose,
//        replyTo (optional: { id, subject } of a reply being answered),
//        onSent (optional, called after the send starts).
import { useState, useEffect, useRef, useMemo } from 'react';
import { GtSelect, GtSearchSelect } from './GtControls';
import GtEditor from './GtEditor';
import { gtConfirm } from './GtConfirm'; // C1-EMAILCONFIRM

const JSON_HEADERS = { 'Content-Type': 'application/json' };
const DEFAULT_BODY = '<p>Hi {first_name},</p><p></p>';
const FIELDS = [
  ['first_name', 'First name'], ['full_name', 'Full name'], ['current_title', 'Title'], ['current_company', 'Company'],
  ['location', 'Location'], ['role_title', 'Role'], ['bench_name', 'Bench'],
];
const plural = (n, one, many) => n + ' ' + (n === 1 ? one : many);
const DOMAIN_PILL = {
  verified: ['gtem-pill--green', 'Verified'], pending: ['gtem-pill--amber', 'Waiting for DNS'], not_started: ['gtem-pill--amber', 'Not verified'],
  failed: ['gtem-pill--rose', 'DNS failed'], temporary_failure: ['gtem-pill--amber', 'DNS check failing'], not_added: ['gtem-pill--rose', 'Not in Resend'], unknown: ['gtem-pill--slate', 'Unknown'],
};
const reSubject = (s) => (/^re:/i.test(s || '') ? s : 'Re: ' + (s || ''));
function localNow(offsetMin) { const d = new Date(Date.now() + (offsetMin || 0) * 60000); d.setSeconds(0, 0); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); }

export default function GtEmailModal({ candidateIds, source, authFetch, onClose, replyTo, onSent }) {
  const [senders, setSenders] = useState(null);
  const [sendersInfo, setSendersInfo] = useState({});
  const [senderId, setSenderId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState(replyTo ? reSubject(replyTo.subject) : '');
  const [body, setBody] = useState(replyTo ? '<p></p>' : DEFAULT_BODY);
  const [later, setLater] = useState(false);
  const [when, setWhen] = useState(localNow(60));
  const [step, setStep] = useState('compose');
  const [check, setCheck] = useState(null);
  const [ticked, setTicked] = useState({});
  const [previewId, setPreviewId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [sent, setSent] = useState(null);
  const [progress, setProgress] = useState(null);
  const [addEmail, setAddEmail] = useState({});
  const [filter, setFilter] = useState('all');
  const [tplName, setTplName] = useState(null); // naming a new template
  const editor = useRef(null);
  const subjectRef = useRef(null);
  const lastFocus = useRef('body');
  const timer = useRef(null);
  const confirming = useRef(false); // a confirm dialogue is open: Escape belongs to it
  const n = candidateIds.length;

  async function loadSenders(keep) {
    try {
      const r = await authFetch('/api/email/senders');
      setSenders(r.senders || []); setSendersInfo({ domains: r.domains, error: r.domain_error });
      if (!keep || !(r.senders || []).some((s) => String(s.id) === String(senderId))) {
        const d = (r.senders || []).find((s) => s.is_default) || (r.senders || [])[0];
        setSenderId(d ? String(d.id) : '');
      }
      if (!(r.senders || []).length) setStep('senders');
    } catch (e) { setSenders([]); setError(e.message); }
  }
  async function loadTemplates() {
    try { const r = await authFetch('/api/email/templates'); setTemplates(r.templates || []); } catch (e) { /* none yet */ }
  }
  useEffect(() => { loadSenders(); loadTemplates(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !busy && !confirming.current) onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);
  async function ask(opts) { confirming.current = true; try { return await gtConfirm(opts); } finally { setTimeout(() => { confirming.current = false; }, 0); } }

  const sender = (senders || []).find((s) => String(s.id) === String(senderId)) || null;

  function insertField(k) {
    const tag = '{' + k + '}';
    if (lastFocus.current === 'subject' && subjectRef.current) {
      const el = subjectRef.current; const a = el.selectionStart || subject.length; const b = el.selectionEnd || a;
      const v = subject.slice(0, a) + tag + subject.slice(b); setSubject(v);
      setTimeout(() => { el.focus(); el.setSelectionRange(a + tag.length, a + tag.length); }, 0);
    } else if (editor.current) { editor.current.insertContent(tag); editor.current.focus(); }
    else setBody((v) => v + tag);
  }
  function pickTemplate(id) {
    setTemplateId(id);
    const t = templates.find((x) => String(x.id) === String(id));
    if (!t) return;
    if (t.subject && !replyTo) setSubject(t.subject);
    if (t.body_html) setBody(t.body_html);
  }
  function insertTemplate() {
    const t = templates.find((x) => String(x.id) === String(templateId));
    if (t && t.body_html && editor.current) { editor.current.insertContent(t.body_html); editor.current.focus(); }
  }
  async function saveTemplate() {
    const name = String(tplName || '').trim();
    if (!name) { setError('Name the template.'); return; }
    try {
      const r = await authFetch('/api/email/templates', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ name, subject, body_html: body }) });
      setTemplates((ts) => ts.concat(r.template).sort((a, b) => a.name.localeCompare(b.name))); setTemplateId(String(r.template.id));
      setNote('Saved as "' + r.template.name + '". It is in the template list for every email from now on.'); setTplName(null);
    } catch (e) { setError(e.message); }
  }
  async function deleteTemplate() {
    const t = templates.find((x) => String(x.id) === String(templateId));
    if (!t) return;
    if (!(await ask({ title: 'Delete template', message: ['"' + t.name + '" is removed from the template list.', 'Emails already sent are not affected.'], confirmLabel: 'Delete', tone: 'danger' }))) return;
    try {
      await authFetch('/api/email/templates/' + t.id, { method: 'DELETE' });
      setTemplates((ts) => ts.filter((x) => x.id !== t.id)); setTemplateId(''); setNote('Template deleted.');
    } catch (e) { setError(e.message); }
  }
  async function updateTemplate() {
    try {
      await authFetch('/api/email/templates/' + templateId, { method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ subject, body_html: body }) });
      await loadTemplates(); setNote('Template updated.');
    } catch (e) { setError(e.message); }
  }

  const payload = (ids) => ({
    candidate_ids: ids || candidateIds, sender_id: senderId || null, subject: subject.trim(), body_html: body,
    source_kind: source && source.kind ? source.kind : 'candidates',
    source_id: source && source.id ? source.id : null,
    source_label: source && source.label ? source.label : null,
    reply_to_reply_id: replyTo ? replyTo.id || null : null,
    thread_id: replyTo ? replyTo.thread_id || null : null,
    template_id: replyTo ? null : (templateId || null), // C1-REPLYRATE
  });

  async function runCheck(pid) {
    if (!sender) { setError('Add a sending address first.'); setStep('senders'); return; }
    if (!subject.trim()) { setError('Add a subject.'); return; }
    if (!body.replace(/<[^>]+>/g, '').trim()) { setError('Write the email first.'); return; }
    setBusy(true); setError(null); setNote(null);
    try {
      const r = await authFetch('/api/email/check', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ ...payload(), preview_id: pid || previewId || null }) });
      setCheck(r);
      if (step !== 'check') {
        const t = {}; r.people.forEach((p) => { t[p.id] = p.can_send; }); setTicked(t);
      }
      setStep('check');
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function sendTest() {
    setBusy(true); setError(null); setNote(null);
    try {
      const r = await authFetch('/api/email/test', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ ...payload(), preview_id: previewId || (check && check.sample && check.sample.id) || null }) });
      setNote('Test sent to ' + r.to + '.');
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function saveEmail(p) {
    const v = String(addEmail[p.id] || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { setError('That does not look like an email address.'); return; }
    setBusy(true); setError(null);
    try {
      await authFetch('/api/candidates/' + p.id, { method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ email: v }) });
      const r = await authFetch('/api/email/check', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ ...payload(), preview_id: previewId || null }) });
      setCheck(r); setTicked((t) => ({ ...t, [p.id]: true }));
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function poll(sendId) {
    try {
      const r = await authFetch('/api/email/sends/' + sendId);
      setProgress(r.send);
      if (['done', 'cancelled'].indexOf(r.send.status) === -1) timer.current = setTimeout(() => poll(sendId), r.send.status === 'scheduled' ? 15000 : 1500);
    } catch (e) { timer.current = setTimeout(() => poll(sendId), 4000); }
  }

  const goIds = check ? check.people.filter((p) => p.can_send && ticked[p.id]).map((p) => p.id) : [];
  async function send() {
    if (!goIds.length) return;
    setBusy(true); setError(null); setNote(null);
    try {
      const at = later && when ? new Date(when).toISOString() : null;
      const r = await authFetch('/api/email/send', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ ...payload(goIds), scheduled_for: at }) });
      setSent(r); setStep('sending'); poll(r.send_id);
      if (onSent) onSent(r);
    } catch (e) { setError(e.message); }
    setBusy(false);
  }
  async function cancelSend() {
    if (!sent) return;
    setBusy(true);
    try { const r = await authFetch('/api/email/sends/' + sent.send_id + '/cancel', { method: 'POST' }); setNote(plural(r.cancelled, 'email', 'emails') + ' not sent.'); poll(sent.send_id); }
    catch (e) { setError(e.message); }
    setBusy(false);
  }

  const people = useMemo(() => (check ? check.people : []), [check]);
  const warned = people.filter((p) => p.can_send && p.warnings.length);
  // C1-EMAIL500: warnings grouped by reason, so a big send is handled in a few clicks.
  const [query, setQuery] = useState('');
  const reasons = useMemo(() => {
    const m = new Map();
    people.forEach((p) => {
      if (!p.can_send) return;
      p.warnings.forEach((w) => {
        const r = m.get(w.key) || { key: w.key, label: String(w.text).split(': ')[0], ids: [] };
        r.ids.push(p.id);
        m.set(w.key, r);
      });
    });
    return Array.from(m.values()).sort((a, b) => b.ids.length - a.ids.length);
  }, [people]);
  const reasonOf = String(filter).indexOf('reason:') === 0 ? String(filter).slice(7) : null;
  const base = reasonOf ? people.filter((p) => p.can_send && p.warnings.some((w) => w.key === reasonOf))
    : filter === 'warned' ? warned : filter === 'cannot' ? people.filter((p) => !p.can_send) : people;
  const q = query.trim().toLowerCase();
  const shown = q ? base.filter((p) => (p.name + ' ' + (p.email || '')).toLowerCase().indexOf(q) !== -1) : base;
  const setMany = (ids, on) => setTicked((t) => { const n = { ...t }; ids.forEach((id) => { n[id] = on; }); return n; });
  // C1-EMAIL500B: summary first for big sends, 200 rows at a time, preview from the list.
  const [listOpen, setListOpen] = useState(false);
  const [limit, setLimit] = useState(200);
  const collapsed = people.length > 50 && !listOpen && !q && !reasonOf && filter === 'all';
  // C1-TIDY20: picking who to preview renders just that person.
  async function previewPerson(id) {
    setPreviewId(id); setError(null);
    try {
      const r = await authFetch('/api/email/preview', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ ...payload(), preview_id: id }) });
      if (r.sample) setCheck((c) => (c ? { ...c, sample: r.sample } : c));
    } catch (e) { setError(e.message); }
  }
  // C1-SEARCHSELECT: the preview picker searches everyone who can be sent to.
  const asOptions = useMemo(() => people.filter((p) => p.can_send).map((p) => ({ value: p.id, label: p.name + (p.email ? ' \u00b7 ' + p.email : '') })), [people]);
  const allTicked = people.filter((p) => p.can_send).every((p) => ticked[p.id]);
  const done = progress && (progress.status === 'done' || progress.status === 'cancelled');
  const handled = progress ? progress.sent + progress.failed : 0;
  const pct = progress && progress.total ? Math.round((handled / progress.total) * 100) : 0;
  const title = step === 'senders' ? 'Sending addresses' : step === 'compose' ? (replyTo ? 'Reply' : 'Email') : step === 'check' ? 'Check and send'
    : progress && progress.status === 'scheduled' ? 'Scheduled' : done ? (progress.status === 'cancelled' ? 'Cancelled' : 'Sent') : 'Sending';

  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal gtsf-modal gtem-modal" role="dialog" aria-modal="true" aria-labelledby="gtem-title" onClick={(e) => e.stopPropagation()}>
        <div className="gtc-modal-head gtsf-head" id="gtem-title">
          <span>{title}</span>
          <span className="gtsf-to">{plural(n, 'person', 'people')}{source && source.label ? ' · ' + source.label : ''}</span>
        </div>

        <div className="gtc-modal-body gtsf-body">
          {error && <div className="gtnt-banner gtnt-banner--rose">{error}</div>}
          {note && <div className="gtnt-banner gtnt-banner--green">{note}</div>}

          {step === 'senders' && (
            <SenderManager senders={senders} info={sendersInfo} authFetch={authFetch} onError={setError} ask={ask}
              onChanged={() => loadSenders(true)} onDone={senders && senders.length ? () => setStep('compose') : null} />
          )}

          {step === 'compose' && (
            <>
              <div className="gtem-row">
                <div className="gtc-field gtem-grow"><span>From</span>
                  {senders === null ? <div className="gtsf-muted">Loading…</div> : (
                    <GtSelect value={senderId} onChange={setSenderId} placeholder="Choose an address"
                      options={senders.map((s) => ({ value: s.id, label: s.display_name + ' <' + s.email + '>' }))} />
                  )}
                </div>
                <button type="button" className="gtsf-linkbtn gtem-manage" onClick={() => { setError(null); setStep('senders'); }}>Manage addresses</button>
              </div>
              {sender && sender.domain_status && sender.domain_status !== 'verified' && (
                <div className="gtnt-banner gtnt-banner--amber"><b>{sender.domain}</b> is not verified in Resend yet ({(DOMAIN_PILL[sender.domain_status] || DOMAIN_PILL.unknown)[1].toLowerCase()}), so emails from it will fail until it is.</div>
              )}
              {!replyTo && (
                <div className="gtem-row">
                  <div className="gtc-field gtem-grow"><span>Template <em className="gtbn-opt">optional</em></span>
                    <GtSelect value={templateId} onChange={pickTemplate} placeholder={templates.length ? 'Start from a template' : 'No templates yet'}
                      options={templates.map((t) => ({ value: t.id, label: t.name + (t.sent ? ' \u00b7 sent to ' + t.sent + ' \u00b7 ' + Math.round((t.replied / t.sent) * 100) + '% replied' : '') }))} />{/* C1-REPLYRATE */}
                  </div>
                  <div className="gtem-tplacts">
                    {templateId && <button type="button" className="gtc-btn-ghost" onClick={insertTemplate} title="Add the template's text where the cursor is">Insert</button>}
                    {templateId && <button type="button" className="gtc-btn-ghost" onClick={updateTemplate}>Update</button>}
                    {templateId && <button type="button" className="gtc-btn-ghost" onClick={deleteTemplate}>Delete</button>}
                    <button type="button" className="gtc-btn-ghost" onClick={() => setTplName(tplName === null ? (subject || '') : null)}>Save as template</button>
                  </div>
                </div>
              )}
              {!replyTo && tplName !== null && (
                <div className="gtem-tplsave">
                  <input autoFocus value={tplName} maxLength={120} placeholder="Template name, e.g. Intro for data roles" onChange={(e) => setTplName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveTemplate(); if (e.key === 'Escape') { e.stopPropagation(); setTplName(null); } }} />
                  <button type="button" className="gtc-btn-ghost" onClick={() => setTplName(null)}>Cancel</button>
                  <button type="button" className="gtc-primary" disabled={!String(tplName).trim()} onClick={saveTemplate}>Save template</button>
                  <span className="gtsf-hint">Saves this subject and message, merge fields included, for any future email.</span>
                </div>
              )}
              <label className="gtc-field"><span>Subject</span>
                <input ref={subjectRef} value={subject} maxLength={300} placeholder="Subject line" onFocus={() => { lastFocus.current = 'subject'; }}
                  onChange={(e) => setSubject(e.target.value)} />
              </label>
              <div className="gtc-field"><span>Message</span>
                <div className="gtem-fields">
                  <span>Insert</span>
                  {FIELDS.map(([k, l]) => <button type="button" key={k} className="gtem-field" onMouseDown={(e) => e.preventDefault()} onClick={() => insertField(k)}>{l}</button>)}
                </div>
                <div className="gtsf-editor" onFocusCapture={() => { lastFocus.current = 'body'; }}>
                  <GtEditor value={body} onChange={setBody} minHeight={260} onReady={(ed) => { editor.current = ed; }} />
                </div>
                <div className="gtsf-hint">Each person gets their own email. <code>{'{first_name}'}</code> reads "there" if we have no first name. {sender && sender.signature_html ? 'Your signature and an unsubscribe line go underneath.' : 'An unsubscribe line goes underneath.'}</div>
              </div>
              <div className="gtem-later">
                <label className="gtem-check"><input type="checkbox" checked={later} onChange={(e) => setLater(e.target.checked)} /><span>Send later</span></label>
                {later && <input type="datetime-local" value={when} min={localNow(2)} onChange={(e) => setWhen(e.target.value)} />}
              </div>
            </>
          )}

          {step === 'check' && check && (
            <>
              <div className="gtsf-tiles">
                <div className="gtsf-tile gtsf-tile--go"><b>{goIds.length}</b><span>will get it</span></div>
                <div className={'gtsf-tile' + (warned.length ? ' gtsf-tile--warn' : '')}><b>{warned.length}</b><span>with something to know</span></div>
                <div className="gtsf-tile"><b>{check.counts.no_email}</b><span>no email address</span></div>
              </div>
              {!check.sending_enabled && <div className="gtnt-banner gtnt-banner--amber"><b>Email is switched off on this server,</b> so nothing will be delivered.</div>}
              {check.domain && check.domain.status !== 'verified' && <div className="gtnt-banner gtnt-banner--amber"><b>{check.domain.name}</b> is not verified in Resend, so these will fail until it is.</div>}
              {check.shared_reputation && <div className="gtnt-banner gtnt-banner--amber">This address shares a domain with sign-in codes and interview invites. A spam mark here would reach those too.</div>}
              {check.unknown_fields.length > 0 && <div className="gtnt-banner gtnt-banner--amber">Not a merge field, so it goes out as typed: {check.unknown_fields.map((f) => '{' + f + '}').join(', ')}</div>}

              {reasons.length > 0 && (
                <div className="gtem-reasons">
                  {reasons.map((r) => {
                    const allOn = r.ids.every((id) => ticked[id]);
                    return (
                      <div key={r.key} className={'gtem-reason' + (reasonOf === r.key ? ' on' : '')}>
                        <b>{r.ids.length}</b><span>{r.label}</span>
                        <button type="button" className="gtsf-linkbtn" onClick={() => setFilter(reasonOf === r.key ? 'all' : 'reason:' + r.key)}>{reasonOf === r.key ? 'Show everyone' : 'Show'}</button>
                        <button type="button" className="gtsf-linkbtn" onClick={() => setMany(r.ids, !allOn)}>{allOn ? 'Untick these' : 'Tick them back'}</button>
                      </div>
                    );
                  })}
                </div>
              )}
              {people.length > 8 && (
                <input className="gtem-psearch" type="search" placeholder={'Find someone in these ' + people.length} value={query} onChange={(e) => setQuery(e.target.value)} />
              )}
              <div className="gtem-people">
                <div className="gtem-peoplehead">
                  <label className="gtem-check"><input type="checkbox" checked={allTicked} onChange={(e) => { const t = {}; people.forEach((p) => { t[p.id] = p.can_send && e.target.checked; }); setTicked(t); }} /><span>Everyone</span></label>
                  <div className="gtem-seg">
                    {[['all', 'All ' + people.length], ['warned', 'To know ' + warned.length], ['cannot', 'Cannot send ' + people.filter((p) => !p.can_send).length]].map(([k, l]) => (
                      <button type="button" key={k} className={filter === k ? 'on' : ''} onClick={() => setFilter(k)}>{l}</button>
                    ))}
                  </div>
                </div>
                {collapsed ? (
                  <button type="button" className="gtem-seeall" onClick={() => setListOpen(true)}>See all {people.length} people</button>
                ) : (
                <div className="gtem-plist">
                  {shown.length === 0 && <div className="gtsf-muted gtem-pempty">Nobody here.</div>}
                  {shown.slice(0, limit).map((p) => (
                    <div key={p.id} className={'gtem-prow' + (p.can_send ? '' : ' gtem-prow--off') + (p.warnings.length ? ' gtem-prow--warn' : '')}>
                      <input type="checkbox" aria-label={'Send to ' + p.name} disabled={!p.can_send} checked={!!(p.can_send && ticked[p.id])} onChange={(e) => setTicked((t) => ({ ...t, [p.id]: e.target.checked }))} />
                      <div className="gtem-pmain">
                        <div className="gtem-pname"><b>{p.name}</b>{p.email ? <span>{p.email}</span> : null}{p.can_send && <button type="button" className="gtsf-linkbtn gtem-pview" disabled={busy} onClick={() => previewPerson(p.id)}>Preview</button>}</div>
                        {p.no_email && (
                          <div className="gtem-addmail">
                            <span>No email address.</span>
                            <input type="email" placeholder="Add one" value={addEmail[p.id] || ''} onChange={(e) => setAddEmail((a) => ({ ...a, [p.id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') saveEmail(p); }} />
                            <button type="button" className="gtc-btn-ghost" disabled={busy || !addEmail[p.id]} onClick={() => saveEmail(p)}>Save</button>
                          </div>
                        )}
                        {p.warnings.length > 0 && <div className="gtem-warns">{p.warnings.map((w) => <span key={w.key} className={'gtem-warn gtem-warn--' + w.key}>{w.text}</span>)}</div>}
                      </div>
                    </div>
                  ))}
                  {shown.length > limit && (
                    <button type="button" className="gtem-seeall" onClick={() => setLimit(limit + 200)}>Show {Math.min(200, shown.length - limit)} more of {shown.length}</button>
                  )}
                </div>
                )}
              </div>

              {check.sample && (
                <div className="gtnt-email">
                  <div className="gtnt-meta">
                    <div className="gtem-asrow"><span>As</span>
                      <div className="gtem-as"><GtSearchSelect value={check.sample.id} onChange={previewPerson} options={asOptions} searchPlaceholder={'Search ' + asOptions.length + ' people by name or email'} /></div>
                    </div>
                    <div><span>From</span><b>{check.sender ? check.sender.display_name + ' <' + check.sender.email + '>' : ''}</b></div>
                    <div><span>Subject</span><b>{check.sample.subject}</b></div>
                  </div>
                  <iframe className="gtnt-preview gtem-preview" title="Preview" sandbox="" srcDoc={
                    '<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;padding:18px 20px;font:14px/1.6 Inter,system-ui,sans-serif;color:#161618}img{max-width:100%}</style></head><body>' + check.sample.html + '</body></html>'
                  } />
                </div>
              )}
              {goIds.length > 0 && (
                <div className="gtsf-muted">
                  {later && when ? 'Goes out ' + new Date(when).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) + '. ' : ''}
                  Emails go out at a steady pace{goIds.length > 100 ? ', and on the free Resend plan up to 100 a day: the rest wait and go after midnight' : ''}. You can close this box once it starts.
                </div>
              )}
            </>
          )}

          {step === 'sending' && (
            <div className="gtsf-progress">
              {progress && progress.status === 'scheduled' ? (
                <div className="gtnt-banner gtnt-banner--slate"><b>Scheduled</b> for {new Date(progress.scheduled_for).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}. {plural(progress.queued, 'email waits', 'emails wait')} until then.</div>
              ) : (
                <>
                  <div className="gtsf-bar"><i style={{ width: (done ? 100 : pct) + '%' }} /></div>
                  {progress ? (
                    <div className="gtsf-pnums">
                      <span><b>{progress.sent}</b> sent</span>
                      <span><b>{progress.failed}</b> failed</span>
                      <span><b>{progress.queued + progress.sending}</b> to go</span>
                    </div>
                  ) : <div className="gtsf-muted">Starting…</div>}
                </>
              )}
              {progress && progress.status === 'paused' && <div className="gtnt-banner gtnt-banner--amber"><b>Paused.</b> {progress.paused_reason}</div>}
              {sent && sent.not_sent && sent.not_sent.length > 0 && (
                <details className="gtsf-list"><summary>{plural(sent.not_sent.length, 'person was', 'people were')} not sent to</summary>
                  <ul>{sent.not_sent.map((x) => <li key={x.id}><b>{x.name}</b> {x.reason}</li>)}</ul></details>
              )}
              {!done && progress && progress.status !== 'scheduled' && <div className="gtsf-muted">Sending carries on if you close this box. Replies land on each person's Emails tab, with a copy to your inbox.</div>}
              {done && progress.status === 'done' && <div className="gtnt-banner gtnt-banner--green"><b>All done.</b> Each email is on the person's record. Replies come back to their Emails tab.</div>}
            </div>
          )}
        </div>

        {step !== 'senders' && <div className="gtc-modal-foot">
          {step === 'compose' && (
            <>
              <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
              <button type="button" className="gtc-primary" disabled={busy || !sender} onClick={() => runCheck()}>{busy ? 'Checking…' : 'Check and preview'}</button>
            </>
          )}
          {step === 'check' && (
            <>
              <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={() => { setNote(null); setStep('compose'); }}>Back</button>
              <button type="button" className="gtc-btn-ghost gtem-testbtn" disabled={busy} onClick={sendTest}>Send me a test</button>
              <button type="button" className="gtc-primary" disabled={busy || !goIds.length || !check.sending_enabled} onClick={send}>
                {busy ? 'Starting…' : (later ? 'Schedule for ' : 'Send to ') + plural(goIds.length, 'person', 'people')}
              </button>
            </>
          )}
          {step === 'sending' && (
            <>
              {progress && ['scheduled', 'paused', 'queued'].indexOf(progress.status) !== -1 && <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={cancelSend}>Cancel the rest</button>}
              <button type="button" className="gtc-primary" onClick={onClose}>Close</button>
            </>
          )}
        </div>}
      </div>
    </div>
  );
}

// Jimmy's sending addresses: name, signature, where reply copies go.
function SenderManager({ senders, info, authFetch, onError, onChanged, onDone, ask }) {
  const blank = { email: '', display_name: '', signature_html: '', forward_to: '', capture_replies: true, is_default: false };
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (senders && !senders.length && !edit) setEdit({ ...blank }); }, [senders]); // eslint-disable-line react-hooks/exhaustive-deps
  const verified = (info && info.domains ? info.domains : []).filter((d) => d.status === 'verified').map((d) => d.name);

  async function save() {
    setBusy(true); onError(null);
    try {
      const b = { email: edit.email, display_name: edit.display_name, signature_html: edit.signature_html || '', forward_to: edit.forward_to || '', capture_replies: !!edit.capture_replies, is_default: !!edit.is_default };
      if (edit.id) await authFetch('/api/email/senders/' + edit.id, { method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(b) });
      else await authFetch('/api/email/senders', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(b) });
      setEdit(null); await onChanged();
    } catch (e) { onError(e.message); }
    setBusy(false);
  }
  async function remove(s) {
    if (!(await ask({ title: 'Remove sending address', message: ['Stop sending from ' + s.email + '?', 'Past emails stay on each record. You can add it back later.'], confirmLabel: 'Remove', tone: 'danger' }))) return;
    try { await authFetch('/api/email/senders/' + s.id, { method: 'DELETE' }); await onChanged(); } catch (e) { onError(e.message); }
  }

  return (
    <div className="gtem-senders">
      {info && info.error && <div className="gtnt-banner gtnt-banner--amber">Could not ask Resend about your domains: {info.error}</div>}
      {senders === null && <div className="gtsf-muted">Loading…</div>}
      {senders && senders.map((s) => {
        const pill = DOMAIN_PILL[s.domain_status] || DOMAIN_PILL.unknown;
        return (
          <div key={s.id} className="gtem-sender">
            <div className="gtem-smain">
              <div className="gtem-sname"><b>{s.display_name}</b><span>{s.email}</span>{s.is_default && <em className="gtem-pill gtem-pill--slate">Default</em>}</div>
              <div className="gtem-smeta">
                <span className={'gtem-pill ' + pill[0]}>{pill[1]}</span>
                {s.capture_replies ? <span>Replies come into GTCO{s.receiving && s.receiving !== 'enabled' ? ' (receiving is not switched on for ' + s.domain + ')' : ''}{s.forward_to ? ', copy to ' + s.forward_to : ''}</span> : <span>Replies go to {s.reply_to || s.email}</span>}
              </div>
            </div>
            <div className="gtem-sacts">
              <button type="button" className="gtc-btn-ghost" onClick={() => setEdit({ ...s, signature_html: s.signature_html || '', forward_to: s.forward_to || '' })}>Edit</button>
              <button type="button" className="gtc-btn-ghost" onClick={() => remove(s)}>Remove</button>
            </div>
          </div>
        );
      })}
      {edit ? (
        <div className="gtem-sform">
          <div className="gtem-row">
            <label className="gtc-field gtem-grow"><span>Email address</span>
              <input value={edit.email} placeholder={verified[0] ? 'jimmy@' + verified[0] : 'jimmy@gtco.net'} onChange={(e) => setEdit({ ...edit, email: e.target.value })} /></label>
            <label className="gtc-field gtem-grow"><span>Name people see</span>
              <input value={edit.display_name} placeholder="Jimmy Braimah" onChange={(e) => setEdit({ ...edit, display_name: e.target.value })} /></label>
          </div>
          {verified.length > 0 && <div className="gtsf-hint">Verified in Resend: {verified.join(', ')}. Any address at these works.</div>}
          <div className="gtc-field"><span>Signature <em className="gtbn-opt">optional</em></span>
            <div className="gtsf-editor"><GtEditor value={edit.signature_html} onChange={(v) => setEdit((x) => ({ ...x, signature_html: v }))} minHeight={150} /></div>
          </div>
          <label className="gtc-field"><span>Send a copy of every reply to</span>
            <input value={edit.forward_to} placeholder="jb@gtco.io" onChange={(e) => setEdit({ ...edit, forward_to: e.target.value })} /></label>
          <label className="gtem-check"><input type="checkbox" checked={!!edit.capture_replies} onChange={(e) => setEdit({ ...edit, capture_replies: e.target.checked })} /><span>Bring replies into GTCO (needs receiving switched on for this domain in Resend)</span></label>
          <label className="gtem-check"><input type="checkbox" checked={!!edit.is_default} onChange={(e) => setEdit({ ...edit, is_default: e.target.checked })} /><span>Use this address by default</span></label>
          <div className="gtem-sformacts">
            {senders && senders.length > 0 && <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={() => setEdit(null)}>Cancel</button>}
            <button type="button" className="gtc-primary" disabled={busy || !edit.email || !edit.display_name} onClick={save}>{busy ? 'Saving…' : 'Save address'}</button>
          </div>
        </div>
      ) : (
        <div className="gtem-sfoot">
          <button type="button" className="gtc-btn-ghost" onClick={() => setEdit({ ...blank })}>Add an address</button>
          {onDone && <button type="button" className="gtc-primary" onClick={onDone}>Back to the email</button>}
        </div>
      )}
    </div>
  );
}
